import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight, User as UserIcon, Users } from "lucide-react";
import { usePublicTenantTheme } from "@/lib/usePublicTenantTheme";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const branding = usePublicTenantTheme();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "", company_name: "", account_type: "individual" });
  const [loading, setLoading] = useState(false);

  // Welcome-email deep-link: `/signup?f=<slug>&email=...&name=...`
  // Pre-fills the form so clients from a firm's invite email don't retype
  // what the firm already knows. Decode once; subsequent edits are user-owned.
  useEffect(() => {
    const email = searchParams.get("email");
    const name = searchParams.get("name");
    if (!email && !name) return;
    setForm((f) => ({
      ...f,
      email: email && !f.email ? email : f.email,
      name: name && !f.name ? name : f.name,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setAccountType = (v) => setForm((f) => ({ ...f, account_type: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const data = await signup(form.name, form.email, form.password, form.company_name, form.account_type);
      toast.success(
        form.account_type === "firm"
          ? "Firm workspace created · 14-day Business trial started"
          : "Workspace created",
      );
      navigate("/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ background: "var(--ll-bg)" }}>
      <div
        className="hidden lg:flex flex-col justify-between p-12 border-r relative ll-grain"
        style={{
          borderColor: "var(--ll-border)",
          background: branding?.brand_color || "#0b5d9e",
          color: "white",
        }}
        data-testid="signup-hero"
      >
        <Link to="/" className="flex items-center gap-2" data-testid="signup-logo">
          {branding?.logo_base64 ? (
            <img src={branding.logo_base64} alt={branding.name || "logo"} className="h-9 w-auto" style={{ maxWidth: 160 }} />
          ) : (
            <div className="w-7 h-7 grid place-items-center bg-white text-[var(--ll-ink)]"><span className="font-display font-bold text-sm">F</span></div>
          )}
          {!branding?.logo_base64 && (
            <span className="font-display font-bold text-lg">{branding?.name || "Financials Ready"}</span>
          )}
        </Link>
        <div>
          {branding?.logo_base64 && (
            <img
              src={branding.logo_base64}
              alt={branding.name || "logo"}
              className="mb-6 h-16 w-auto"
              style={{ maxWidth: 260 }}
              data-testid="signup-hero-logo"
            />
          )}
          <div className="font-display font-bold text-4xl xl:text-5xl leading-tight" data-testid="signup-hero-title">
            {branding?.name ? <>Join {branding.name}.<br />7 days free.</> : <>7 days free.<br />No card required.</>}
          </div>
          <p className="mt-4 text-white/70 max-w-md">
            {branding?.name
              ? `${branding.name} invited you — set up your workspace in under two minutes.`
              : "Set up your workspace in under two minutes. Invite your accountant later — we keep the books clean either way."}
          </p>
        </div>
        <div className="text-xs uppercase tracking-[0.25em] text-white">Made for finance</div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="signup-form">
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] mb-3">Start free</div>
          <h1 className="font-display font-bold text-3xl tracking-tight mb-6">Create your workspace.</h1>

          {/* Account-type toggle — picks between a single-company workspace and
              a bookkeeping-firm workspace that unlocks the Clients module on a
              14-day Business trial. Mirrors the server-side `account_type` field. */}
          <div className="mb-6" data-testid="signup-account-type">
            <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-2">Account type</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setAccountType("individual")}
                data-testid="account-type-individual"
                className={`p-3 border text-left transition-colors ${form.account_type === "individual" ? "border-[var(--ll-ink)] bg-[var(--ll-mint)]" : "border-[var(--ll-border)] hover:bg-[var(--ll-mint)]/50"}`}
              >
                <div className="flex items-center gap-1.5 mb-0.5">
                  <UserIcon size={13} />
                  <div className="text-sm font-medium">Individual</div>
                </div>
                <div className="text-[11px] text-[var(--ll-ink-soft)] leading-snug">
                  One company. Trial plan.
                </div>
              </button>
              <button
                type="button"
                onClick={() => setAccountType("firm")}
                data-testid="account-type-firm"
                className={`p-3 border text-left transition-colors ${form.account_type === "firm" ? "border-[var(--ll-ink)] bg-[var(--ll-mint)]" : "border-[var(--ll-border)] hover:bg-[var(--ll-mint)]/50"}`}
              >
                <div className="flex items-center gap-1.5 mb-0.5">
                  <Users size={13} />
                  <div className="text-sm font-medium">Multi-client firm</div>
                </div>
                <div className="text-[11px] text-[var(--ll-ink-soft)] leading-snug">
                  Serve many clients. 14-day Business trial.
                </div>
              </button>
            </div>
          </div>

          {[
            { k: "name", label: "Your name", type: "text", placeholder: "Jane Cooper", testid: "signup-name-input" },
            { k: "company_name", label: form.account_type === "firm" ? "Firm name" : "Company name", type: "text", placeholder: form.account_type === "firm" ? "Cooper Bookkeeping" : "Cooper & Co.", testid: "signup-company-input" },
            { k: "email", label: "Email", type: "email", placeholder: "you@company.com", testid: "signup-email-input" },
            { k: "password", label: "Password", type: "password", placeholder: "••••••••", testid: "signup-password-input" },
          ].map((f) => (
            <div key={f.k} className="mb-4">
              <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">{f.label}</label>
              <input
                type={f.type} required value={form[f.k]} onChange={onChange(f.k)}
                data-testid={f.testid}
                placeholder={f.placeholder}
                className="w-full px-3 py-2.5 border bg-white" style={{ borderColor: "var(--ll-border)" }}
              />
            </div>
          ))}
          <button
            type="submit" disabled={loading}
            data-testid="signup-submit-btn"
            className="mt-2 w-full bg-[var(--ll-ink)] text-white py-3 hover:bg-[#2d6a4f] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading ? "Creating…" : (
              <>{form.account_type === "firm" ? "Start firm trial" : "Create workspace"} <ArrowRight size={15} /></>
            )}
          </button>
          <div className="mt-6 text-sm text-[var(--ll-ink-soft)]">
            Already have an account? <Link to="/login" className="text-[var(--ll-ink)] underline underline-offset-4" data-testid="link-to-login">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
